import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Banknote, Loader2 } from 'lucide-react';
import PayoutBankDetailsForm from './PayoutBankDetailsForm';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * AdminBankDetailsModal — admin view + edit of any user's payout bank details.
 * Opens from the Royalty Report "View bank" button on each author row.
 *
 * Props:
 *   • user_id  — the author user id whose details to load
 *   • onClose  — modal close handler
 */
const AdminBankDetailsModal = ({ user_id, onClose }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [bank, setBank] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!user_id) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/payout-bank-details/${user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setUser(res.data?.user || null);
        setBank(res.data?.bank || null);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast({
            title: 'Could not load bank details',
            description: e?.response?.data?.detail || 'Try again.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user_id, refreshTick, toast]);

  const onSubmit = async (form) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/admin/payout-bank-details/${user_id}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBank(res.data);
      setRefreshTick((t) => t + 1);
      toast({
        title: 'Bank details saved',
        description: `${user?.name || 'Author'}'s bank info has been updated.`,
      });
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      data-testid="admin-bank-modal"
    >
      <div className="bg-gray-900 rounded-xl w-full max-w-3xl max-h-[90vh] border border-white/10 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Banknote className="h-4 w-4 text-emerald-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Author payout bank details</h3>
              <p className="text-xs text-gray-400">
                {user ? (
                  <>
                    {user.name} · <span className="text-gray-500">{user.email}</span>
                  </>
                ) : (
                  <span className="text-gray-500">Loading user…</span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="admin-bank-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : notFound ? (
            <div className="text-center py-12 space-y-2">
              <p className="text-amber-300 text-sm font-medium">
                That author account no longer exists.
              </p>
              <p className="text-gray-400 text-xs">
                The book is still in the catalog but its linked author user has been
                deleted. Re-link a current user from User Management → Books.
              </p>
            </div>
          ) : (
            <PayoutBankDetailsForm
              key={bank?.updated_at || 'new'}
              initial={bank}
              onSubmit={onSubmit}
              submitting={submitting}
              submitLabel="Save (on behalf of author)"
              helperNote={
                bank?.updated_at
                  ? `Last updated ${new Date(bank.updated_at).toLocaleString()}${
                      bank.updated_by ? ` by ${bank.updated_by === user_id ? 'author' : 'admin'}` : ''
                    }.`
                  : 'No details on file yet.'
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBankDetailsModal;
